import React from "react";
import Modal from "react-modal";
import moment from "moment";
import { MdClose, MdWarning } from "react-icons/md";
import "./style.css";

const customStyles = {
  content: {
    top: "50%",
    left: "50%",
    right: "auto",
    bottom: "auto",
    marginRight: "-50%",
    transform: "translate(-50%, -50%)",
    maxWidth: "520px",
    width: "90%",
    maxHeight: "80vh",
    padding: "0",
    borderRadius: "12px",
    border: "none",
    boxShadow: "0 20px 40px -12px rgba(0, 0, 0, 0.25)",
    overflow: "hidden",
  },
  overlay: {
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    zIndex: 1000,
  },
};

Modal.setAppElement("#root");

// Calculate importer price - handles both wholesale and one-piece orders
// For mixed orders, matches orderDetails with wholesale breakdown by productId
// Priority: finalPrice > breakdown.FBP for one-piece orders
const calculateImporterrPrice = (order) => {
  const shippingCharges = Number(order?.totalShippingCharges) || 0;
  
  // Build a map of productId -> wholesale FBP
  const wholesaleBreakdownMap = new Map();
  if (Array.isArray(order?.breakdown)) {
    for (const breakdownItem of order.breakdown) {
      if (breakdownItem?.pricingMode === 'wholesale' && breakdownItem?.productId && breakdownItem?.breakdown?.[0]?.FBP) {
        wholesaleBreakdownMap.set(breakdownItem.productId.toString(), Number(breakdownItem.breakdown[0].FBP) || 0);
      }
    }
  }
  
  let productTotal = 0;
  
  // Helper function to get price for one-piece item - prioritize finalPrice over breakdown.FBP
  const getOnePiecePrice = (detail) => {
    const variant = detail?.variant;
    // Priority: finalPrice > breakdown.FBP
    if (variant?.finalPrice != null && Number(variant.finalPrice) > 0) {
      return Number(variant.finalPrice);
    }
    const breakdown = variant?.breakdown;
    return Number(breakdown?.FBP ?? breakdown?.fbp) || 0;
  };
  
  // If we have wholesale breakdown and product info in orderDetails, use per-product matching
  if (wholesaleBreakdownMap.size > 0) {
    // Group orderDetails by product to match with wholesale breakdown
    const productGroups = new Map();
    (order?.orderDetails || []).forEach(detail => {
      // Get productId from populated product or from the reference
      const productId = detail?.product?._id?.toString() || detail?.product?.toString() || '';
      if (!productGroups.has(productId)) {
        productGroups.set(productId, []);
      }
      productGroups.get(productId).push(detail);
    });
    
    // Calculate total for each product group
    productGroups.forEach((details, productId) => {
      const wholesaleFBP = wholesaleBreakdownMap.get(productId);
      if (wholesaleFBP) {
        // Wholesale product - use wholesale breakdown FBP (already includes quantity)
        productTotal += wholesaleFBP;
      } else {
        // One-piece product - use finalPrice or fallback to breakdown.FBP * quantity
        details.forEach(detail => {
          const price = getOnePiecePrice(detail);
          const quantity = Number(detail?.quantity) || 1;
          productTotal += price * quantity;
        });
      }
    });
  } else {
    // No wholesale breakdown - pure one-piece order
    (order?.orderDetails || []).forEach(detail => {
      const price = getOnePiecePrice(detail);
      const quantity = Number(detail?.quantity) || 1;
      productTotal += price * quantity;
    });
  }
  
  return { productTotal, shippingCharges, total: productTotal + shippingCharges };
};

const CancelOrderModal = ({ isOpen, onClose, order, orders, onConfirm, isLoading }) => {
  // Support both single order and multiple orders
  const orderList = orders && orders.length > 0 ? orders : (order ? [order] : []);
  const isMultiple = orderList.length > 1;

  if (orderList.length === 0) return null;

  // Calculate price for a single order
  const calculateOrderPrice = (ord) => {
    if (!ord) return { productTotal: 0, shippingCharges: 0, total: 0 };
    return calculateImporterrPrice(ord);
  };

  // Calculate totals for all orders
  const calculateTotals = () => {
    let totalProduct = 0;
    let totalShipping = 0;

    orderList.forEach((ord) => {
      const prices = calculateOrderPrice(ord);
      totalProduct += prices.productTotal;
      totalShipping += prices.shippingCharges;
    });

    return {
      productTotal: totalProduct,
      shippingCharges: totalShipping,
      total: totalProduct + totalShipping,
    };
  };

  const totals = calculateTotals();

  const formatCurrency = (value) => {
    const num = Number(value);
    if (!Number.isFinite(num)) return "₹0.00";
    return `₹${num.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // Get order IDs for cancellation
  const getOrderIds = () => {
    return orderList.map((ord) => ord._id);
  };

  // Check if any order is processing (for refund message)
  const hasProcessingOrders = orderList.some((ord) => ord.status === "processing");

  return (
    <Modal isOpen={isOpen} onRequestClose={onClose} style={customStyles} contentLabel="Cancel Order">
      <div className="cancel-modal">
        {/* Header */}
        <div className="cancel-modal-header">
          <div className="header-icon">
            <MdWarning />
          </div>
          <h2>{isMultiple ? `Cancel ${orderList.length} Orders` : "Cancel Order"}</h2>
          <button className="close-btn" onClick={onClose}>
            <MdClose />
          </button>
        </div>

        {/* Body */}
        <div className="cancel-modal-body">
          {/* Single Order Details */}
          {!isMultiple && orderList[0] && (
            <div className="order-info-section">
              <div className="info-row">
                <span className="label">Order ID:</span>
                <span className="value">{orderList[0].upsaleoOrderId || orderList[0].orderId}</span>
              </div>
              <div className="info-row">
                <span className="label">MKP Order ID:</span>
                <span className="value">{orderList[0].orderId}</span>
              </div>
              <div className="info-row">
                <span className="label">Order Date:</span>
                <span className="value">{moment(orderList[0].orderDate).format("Do MMM YYYY, LT")}</span>
              </div>
              {orderList[0].paidDate && (
                <div className="info-row">
                  <span className="label">Paid Date:</span>
                  <span className="value">{moment(orderList[0].paidDate).format("Do MMM YYYY, LT")}</span>
                </div>
              )}
              <div className="info-row">
                <span className="label">Status:</span>
                <span className={`status-badge status-${orderList[0].status?.toLowerCase().replace(' ', '-')}`}>
                  {orderList[0].status}
                </span>
              </div>
            </div>
          )}

          {/* Multiple Orders List */}
          {isMultiple && (
            <div className="orders-list-section">
              <h3>Selected Orders ({orderList.length})</h3>
              <div className="orders-list">
                {orderList.map((ord, index) => {
                  const prices = calculateOrderPrice(ord);
                  const isProcessing = ord.status === "processing";
                  return (
                    <div key={ord._id || index} className="order-item">
                      <div className="order-item-header">
                        <span className="order-id">{ord.upsaleoOrderId || ord.orderId}</span>
                        <span className={`status-badge status-${ord.status?.toLowerCase().replace(' ', '-')}`}>
                          {ord.status}
                        </span>
                      </div>
                      {isProcessing && (
                        <div className="order-item-details">
                          <span>Product: {formatCurrency(prices.productTotal)}</span>
                          {prices.shippingCharges > 0 && (
                            <span>Shipping: {formatCurrency(prices.shippingCharges)}</span>
                          )}
                          <span className="order-total">Total: {formatCurrency(prices.total)}</span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Price Breakdown - Single Order (only show for processing orders) */}
          {!isMultiple && hasProcessingOrders && (
            <div className="price-section">
              <h3>Refund Details</h3>
              <div className="price-row">
                <span>Product Total:</span>
                <span>{formatCurrency(totals.productTotal)}</span>
              </div>
              {totals.shippingCharges > 0 && (
                <div className="price-row">
                  <span>Shipping Charges:</span>
                  <span>{formatCurrency(totals.shippingCharges)}</span>
                </div>
              )}
              <div className="price-row total">
                <span>Total Refund:</span>
                <span className="refund-amount">{formatCurrency(totals.total)}</span>
              </div>
            </div>
          )}

          {/* Price Summary - Multiple Orders (only show if any processing orders) */}
          {isMultiple && hasProcessingOrders && (
            <div className="price-section summary">
              <h3>Total Refund Summary</h3>
              <div className="price-row">
                <span>Total Product Amount:</span>
                <span>{formatCurrency(totals.productTotal)}</span>
              </div>
              {totals.shippingCharges > 0 && (
                <div className="price-row">
                  <span>Total Shipping Charges:</span>
                  <span>{formatCurrency(totals.shippingCharges)}</span>
                </div>
              )}
              <div className="price-row total">
                <span>Grand Total Refund:</span>
                <span className="refund-amount">{formatCurrency(totals.total)}</span>
              </div>
            </div>
          )}

          {/* Warning Message */}
          <div className="warning-message">
            <p>Are you sure you want to cancel {isMultiple ? "these orders" : "this order"}?</p>
            {hasProcessingOrders && (
              <p className="refund-note">The refund amount will be credited to your wallet.</p>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="cancel-modal-footer">
          <button className="btn-secondary" onClick={onClose} disabled={isLoading}>
            Keep {isMultiple ? "Orders" : "Order"}
          </button>
          <button 
            className="btn-danger" 
            onClick={() => onConfirm(getOrderIds())} 
            disabled={isLoading}
          >
            {isLoading ? "Cancelling..." : `Confirm Cancel${isMultiple ? ` (${orderList.length})` : ""}`}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default CancelOrderModal;
